import Button from '@components/V4/Button';
import Input from '@components/V4/Inputs/Input';
import Select from '@components/V4/Select/Select';
import { CONTRACT_STATUS_FILTER_OPTIONS } from '@shared/constants';
import React from 'react';

interface ContractsHeaderProps {
  searchParams: any;
  setSearchParams: (data: Partial<any>) => void;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  strategists?: { _id: string; fullName: string }[];
  onNewContract: () => void;
  onExport: () => void;
  isExporting: boolean;
}

const CHURN_OPTIONS = [
  { label: 'Any churn risk', value: null },
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

const OVERDUE_OPTIONS = [
  { label: 'All rows', value: 'unchecked' },
  { label: 'Overdue only', value: 'checked' },
];

const HAS_CONTRACT_OPTIONS = [
  { label: 'All students', value: null },
  { label: 'With contract', value: 'with' },
  { label: 'Without contract', value: 'without' },
];

const DEACTIVATED_OPTIONS = [
  { label: 'Active students', value: 'unchecked' },
  { label: 'Include deactivated', value: 'checked' },
];

const ContractsHeader = (props: ContractsHeaderProps) => {
  const {
    searchParams,
    setSearchParams,
    searchValue,
    setSearchValue,
    strategists,
    onNewContract,
    onExport,
    isExporting,
  } = props;

  const selectedStatuses: string[] = searchParams.status
    ? String(searchParams.status).split(',')
    : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72 max-w-full">
          <Input
            type="text"
            name="contracts-search"
            id="contracts-search"
            label="Search"
            placeholder="Student, parent, email, contract no."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <div className="w-56 max-w-full">
          <Select
            variant="secondary"
            label="Status"
            mode="multiple"
            value={selectedStatuses.length ? selectedStatuses : undefined}
            options={CONTRACT_STATUS_FILTER_OPTIONS}
            onChange={(values) =>
              setSearchParams({
                status: Array.isArray(values) && values.length ? values.join(',') : null,
                page: 1,
              })
            }
            onClear={() => setSearchParams({ status: null, page: 1 })}
            placeholder="Any status"
            allowClear
          />
        </div>
        <div className="w-56 max-w-full">
          <Select
            variant="secondary"
            label="Strategist"
            value={searchParams.strategist ?? null}
            options={[
              { label: 'Any strategist', value: null },
              ...(strategists ?? []).map((strategist) => ({
                label: strategist.fullName,
                value: strategist._id,
              })),
            ]}
            onSelect={(value) => setSearchParams({ strategist: value || null, page: 1 })}
          />
        </div>
        <div className="w-44 max-w-full">
          <Select
            variant="secondary"
            label="Churn risk"
            value={searchParams.churnLevel ?? null}
            options={CHURN_OPTIONS}
            onSelect={(value) => setSearchParams({ churnLevel: value || null, page: 1 })}
          />
        </div>
        <div className="w-44 max-w-full">
          <Select
            variant="secondary"
            label="Overdue"
            value={searchParams.overdueOnly ?? 'unchecked'}
            options={OVERDUE_OPTIONS}
            onSelect={(value) => setSearchParams({ overdueOnly: value, page: 1 })}
          />
        </div>
        <div className="w-44 max-w-full">
          <Select
            variant="secondary"
            label="Contract"
            value={searchParams.hasContract ?? null}
            options={HAS_CONTRACT_OPTIONS}
            onSelect={(value) => setSearchParams({ hasContract: value || null, page: 1 })}
          />
        </div>
        <div className="w-48 max-w-full">
          <Select
            variant="secondary"
            label="Students"
            value={searchParams.showDeactivated ?? 'unchecked'}
            options={DEACTIVATED_OPTIONS}
            onSelect={(value) => setSearchParams({ showDeactivated: value, page: 1 })}
          />
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            variant="secondary"
            buttonText="Export CSV"
            isLoading={isExporting}
            onClick={onExport}
          />
          <Button variant="primary" buttonText="New contract" onClick={onNewContract} />
        </div>
      </div>
    </div>
  );
};

export default ContractsHeader;
